import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, PencilIcon, Text } from '@pimm/base';
import { Box, HStack, VStack, useTheme } from 'native-base';
import { size } from 'lodash';

import { formatTo, stringToDateLocal } from '@app/utils/date-formatter';
import { usePositioningPlan } from '../context';

type PositioningPlanConfirmationProps = {
  _container?: ComponentProps<typeof VStack>;
  isDisabled?: boolean;
  onOpenSchedules?: () => void;
};

export const PositioningPlanConfirmation = ({ _container, isDisabled, ...props }: PositioningPlanConfirmationProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { positioning } = usePositioningPlan();

  const countEmployees = size(positioning?.employees) ?? 0;
  const confirmedTime = positioning?.confirmedTime ? stringToDateLocal(positioning.confirmedTime) : undefined;

  return (
    <VStack space={1} pt={2} px={3} {..._container}>
      {!!confirmedTime && (
        <Box pt={2}>
          <Text size="xs" fontWeight={500} color="gray.600" mt={-2}>
            {t('positioning_plan:last_update')}{' '}
            <Text size="xs" fontWeight={600} color="gray.700">
              {formatTo(confirmedTime, 'lll')}
            </Text>
          </Text>
        </Box>
      )}

      <HStack
        w="full"
        alignItems="center"
        space={2}
        px={{ md: 3, xl: 4 }}
        minHeight={{ md: 50, xl: 60 }}
        borderColor="gray.300"
        borderWidth={1}
        bgColor="gray.50"
        borderRadius="lg"
      >
        <Text size="9xl" fontWeight={700} color="gray.900">
          {countEmployees}
        </Text>
        <Text flex={1} size="md" fontWeight={600} color="gray.700" lineHeight="xs">
          {t('positioning_plan:scheduled')}
          {'\n'}
          {t('positioning_plan:employees')}
        </Text>

        <Button
          variant="unstyled"
          p={0}
          minHeight={{ md: 8, xl: 9 }}
          minW={{ md: 8, xl: 9 }}
          bg="white"
          isDisabled={isDisabled}
          _hover={{ bg: 'white' }}
          onPress={props.onOpenSchedules}
          startIcon={<PencilIcon size={{ base: '16px', xl: 5 }} color={colors.gray[700]} />}
        />
      </HStack>
    </VStack>
  );
};
